import type { Block } from "../content/types";
import { passage as corinthians13 } from "./1-corinthians-13";
import { passage as timothy17 } from "./2-timothy-1-7";
import { passage as isaiah4031 } from "./isaiah-40-31";
import { passage as isaiah4110 } from "./isaiah-41-10";
import { passage as jeremiah2911 } from "./jeremiah-29-11";
import { passage as john316 } from "./john-3-16";
import { passage as matthew633 } from "./matthew-6-33";
import { passage as matthew1128 } from "./matthew-11-28-30";
import { passage as philippians467 } from "./philippians-4-6-7";
import { passage as philippians413 } from "./philippians-4-13";
import { passage as proverbs356 } from "./proverbs-3-5-6";
import { passage as psalm23 } from "./psalm-23";
import { passage as psalm4610 } from "./psalm-46-10";
import { passage as psalm91 } from "./psalm-91";
import { passage as romans828 } from "./romans-8-28";
import { passage as romans122 } from "./romans-12-2";
import type { Passage } from "./types";

/**
 * The passages, in the order `/verse` lists them.
 *
 * The first eight are the overlap of the two published datasets in
 * `docs/BIBLE-SEARCH-DEMAND.md` — the verses near the top of BOTH the app
 * rankings and the web-reader rankings. The eight after them are the next tier
 * of the same evidence, and every one of them had to clear the section's real
 * bar before it was written: a `misreading` worth correcting. A passage nobody
 * misreads does not need a fifty-first page about it, however popular it is.
 */
export const PASSAGES: Passage[] = [
  isaiah4110,
  jeremiah2911,
  psalm23,
  psalm91,
  philippians467,
  philippians413,
  proverbs356,
  romans122,
  john316,
  romans828,
  psalm4610,
  matthew633,
  isaiah4031,
  matthew1128,
  timothy17,
  corinthians13,
];

export const PASSAGES_BY_SLUG: Record<string, Passage> = Object.fromEntries(
  PASSAGES.map((p) => [p.slug, p]),
);

export function passageBySlug(slug: string): Passage | undefined {
  return PASSAGES_BY_SLUG[slug];
}

/**
 * The path a passage lives at.
 *
 * `/verse/<ref>` singular, deliberately next to `/verses/<topic>` plural — see
 * `types.ts`. Nothing hand-writes either one; every link comes from here or
 * from `versePath`, so the two cannot be confused into each other.
 */
export function passagePath(slug: string): string {
  return `/verse/${slug}`;
}

/**
 * Roughly how long the page takes to read.
 *
 * Counts EVERYTHING that renders, including the takeaways and the FAQ. Those
 * are skipped by the equivalent helpers on the older corpora, where they are a
 * rounding error; here they are a real share of the page, because a passage
 * page is short prose around a lot of short answers. A reading time that
 * ignores a third of the words is not an estimate, it is a wrong number.
 */
export function passageMinutes(item: Passage): number {
  const prose = [...item.body, ...item.use].reduce(
    (n, block) => n + blockWords(block),
    0,
  );
  const quotes =
    count(item.verse.text) +
    item.context.reduce((n, verse) => n + count(verse.text), 0);
  const correction =
    count(item.misreading.claim) +
    item.misreading.correction.reduce((n, p) => n + count(p), 0);
  const answers =
    item.takeaways.reduce((n, line) => n + count(line), 0) +
    (item.faq ?? []).reduce(
      (n, entry) => n + count(entry.q) + count(entry.a),
      0,
    );
  return Math.max(1, Math.round((prose + quotes + correction + answers) / 220));
}

function count(text: string): number {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

function blockWords(block: Block): number {
  switch (block.type) {
    case "ul":
    case "ol":
      return count(block.items.join(" "));
    case "verse":
      return count(block.verse.text);
    default:
      return count(block.text);
  }
}

/** The `<h2>`s of a passage's prose, for its table of contents. */
export function passageOutline(item: Passage): { id: string; text: string }[] {
  return [...item.body, ...item.use].flatMap((block) =>
    block.type === "h2" ? [{ id: block.id, text: block.text }] : [],
  );
}

export type { Passage } from "./types";
